<template>
  <div
    v-observe-visibility="visibilityOptions"
    class="a-top-brokers__wrapper"
    :class="dynamicBrokerLayoutClass"
  >
    <div class="a-top-brokers__title-disclaimer-sponsored-container">
      <h2 class="a-top-brokers__title subtitle-3">
        Top Brokers
      </h2>
      <div class="a-top-brokers__disclaimer-sponsored-container">
        <p class="a-top-brokers__sponsored-label text-small">
          Sponsored
        </p>
        <a-disclaimer
          :layout-ordering="$options.consts.disclaimerLayoutOrdering"
          :popup-settings-by-breakpoint="disclaimerPopupSettingsByBreakpoint"
          :top-space-offset="$options.consts.disclaimerTopSpaceOffset"
          show-beak
        >
          <template v-slot:activator-content
            ><div class="a-top-brokers__disclaimer-icon-text-container">
              <a-icon
                :icon="$options.consts.ICON.WARNING_CIRCLE"
                :width="16"
                :height="16"
                class="a-top-brokers__disclaimer-icon"
              />
              <span class="a-top-brokers__disclaimer-label text-small">
                General Risk Warning
              </span>
            </div></template
          >
          <template v-slot:content>
            <div class="a-top-brokers__disclaimer-text">
              {{ $options.consts.DISCLAIMER_TEXT }}
            </div>
          </template>
        </a-disclaimer>
      </div>
    </div>
    <div class="a-top-brokers__broker-items">
      <div
        v-for="(broker, index) in topBrokersToRender"
        :key="`${broker.Id}-${index}`"
      >
        <div
          class="a-top-brokers__broker-container"
          v-if="!isPlaceholder(broker)"
        >
          <div class="a-top-brokers__broker-image-wrapper">
            <a-image
              :image="broker.Logo"
              :url="broker.Url"
              :aspect-ratio="
                $options.consts.aspectRatios.ilDirectoryCompanyLogo
              "
              class="a-top-brokers__broker-image"
            />
          </div>
          <div class="a-top-brokers__broker-title-button">
            <p class="a-top-brokers__broker-title">
              {{ broker.CompanyName }}
            </p>
            <a-button
              :button-style="$options.consts.BUTTON_STYLE.PURPLE"
              :href="broker.Url"
              :open-in-new-tab="true"
              class="a-top-brokers__broker-button"
            >
              Open Account
            </a-button>
          </div>
        </div>
        <a-link v-else :to="broker.Url" aria-label="Go to Contact Us Page">
          <div
            :style="getPlaceholderBgImage"
            class="a-top-brokers__placeholder-container"
          ></div>
        </a-link>
      </div>
    </div>
  </div>
</template>

<script>
import { aspectRatios } from 'enums/aspectRatios'
import { LAYOUT as DISCLAIMER_LAYOUT } from 'shared/ADisclaimer/enums'
import AIcon, { ICON } from 'shared/AIcon'
import ADisclaimer from 'shared/ADisclaimer/index.vue'
import { guid, getLinkToIlBucketFile } from '@/plugins/helper'
import { ROUTE } from 'enums/routes'
import { propValidator, PROP_TYPES } from '@/utils/validators'
import { BUTTON_STYLE } from 'shared/AButton/index'
import ALink from 'shared/ALink'
import mixins from '@/utils/mixins'

const DISCLAIMER_TEXT =
  'investingLive is a market news and analysis platform that lists and reviews forex brokers for informational use only. We do not provide investment advice or brokerage services. CFDs are complex, high-risk products and may not be suitable for all investors. investingLive is not responsible for any trading losses or decisions made based on content found on our site.'

const FIRST_ROW_START = 0
const FIRST_ROW_END = 2
const SECOND_ROW_START = 3
const SECOND_ROW_END = 5

const fillWithPlaceholders = (items, start, end) => {
  const hasItemsInRange = items.some(
    ({ Order }) => Order >= start && Order <= end
  )
  if (!hasItemsInRange) return []

  const itemsMap = new Map(items.map(item => [item.Order, item]))

  return Array.from({ length: end - start + 1 }, (_, i) => {
    const order = start + i
    return itemsMap.get(order) ?? createPlaceholder(order)
  })
}

const createPlaceholder = order => ({
  Id: guid(),
  Url: ROUTE.CONTACT_US,
  Order: order,
  IsPlaceholder: true,
  Logo: {
    MediaId: guid(),
    ModifiedOn: '0000-00-00T00:00:00.000Z',
    MediaType: 'Image',
    Title: 'Promote Your Brokerage Here',
    Caption: 'Promote Your Brokerage Here',
    AltText: 'Promote Your Brokerage Here',
    IsOld: false
  }
})

export default {
  name: 'ATopBrokers',
  mixins: [mixins.lazyHydratedComponentState],
  components: {
    ADisclaimer,
    AIcon,
    ALink
  },
  props: {
    sidebarLayout: propValidator([PROP_TYPES.BOOLEAN], false, false),
    topBrokers: propValidator([PROP_TYPES.ARRAY], false, () => [])
  },
  consts: {
    aspectRatios,
    ICON,
    DISCLAIMER_TEXT,
    disclaimerTopSpaceOffset: -20,
    disclaimerLayoutOrdering: [
      DISCLAIMER_LAYOUT.BOTTOM_LEFT,
      DISCLAIMER_LAYOUT.TOP_LEFT,
      DISCLAIMER_LAYOUT.TOP_RIGHT,
      DISCLAIMER_LAYOUT.BOTTOM_RIGHT
    ],
    BUTTON_STYLE
  },
  data() {
    return {
      visibilityOptions: {
        callback: this.onVisibilityStateChange,
        once: true
      },
      disclaimerPopupSettingsByBreakpoint: {
        [this.$breakpoint.aboveDesktopXl]: {
          modalWidth: 244,
          modalHeight: 216
        },
        [this.$breakpoint.mobile]: {
          modalWidth: 244,
          modalHeight: 216
        }
      },
      isPlaceholderImageVisible: false
    }
  },
  computed: {
    topBrokersToRender() {
      return [
        ...fillWithPlaceholders(
          this.topBrokers,
          FIRST_ROW_START,
          FIRST_ROW_END
        ),
        ...fillWithPlaceholders(
          this.topBrokers,
          SECOND_ROW_START,
          SECOND_ROW_END
        )
      ]
    },
    dynamicBrokerLayoutClass() {
      return {
        'a-top-brokers__wrapper--homepage': !this.sidebarLayout,
        'a-top-brokers__wrapper--sidebar': this.sidebarLayout
      }
    },
    getPlaceholderBgImage() {
      return {
        backgroundImage: this.isPlaceholderImageVisible
          ? `url(${getLinkToIlBucketFile('top-broker-placeholder-il-v3.jpg')})`
          : null
      }
    }
  },
  methods: {
    isPlaceholder(broker) {
      return broker.IsPlaceholder
    },
    onVisibilityStateChange(isVisible) {
      if (!isVisible) return

      this.showPlaceholderBackgroundImages()
    },
    showPlaceholderBackgroundImages() {
      this.isPlaceholderImageVisible = true
    }
  },
  mounted() {
    this.$emit('mounted')
  }
}
</script>

<style lang="scss" scoped>
$disclaimer-sponsored-color: $c--gray;

.a-top-brokers__wrapper--homepage {
  background: $c--gray-soft;
  border-radius: 6px;
  width: auto;
  padding: 24px;

  .a-top-brokers__title-disclaimer-sponsored-container {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 24px;

    @include mobile {
      flex-direction: column;
      align-items: flex-start;
    }
  }

  .a-top-brokers__broker-items {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    column-gap: 18px;
    row-gap: 12px;

    @include desktop-sm {
      grid-template-columns: repeat(2, 1fr);
    }

    @include mobile {
      display: flex;
      flex-direction: column;
    }
  }

  .a-top-brokers__broker-container {
    transition: none;
    padding: 16px;
    border-radius: 6px;
    border: 1px solid $c--gray-light;
    overflow: hidden;
    background: $c--white;
    text-align: center;
    display: flex;

    @include mobile {
      flex-direction: column;
      align-items: center;
    }
  }

  .a-top-brokers__broker-container {
    .a-top-brokers__broker-image-wrapper {
      width: 148px;
      margin-right: 12px;

      @include mobile {
        margin-right: 0;
        margin-bottom: 8px;
      }
    }
  }

  .a-top-brokers__placeholder-container {
    min-height: 105px;
  }

  .a-top-brokers__broker-image-wrapper {
    width: 148px;
  }

  .a-top-brokers__broker-title-button {
    text-align: left;

    @include mobile {
      text-align: center;
    }
  }
}

.a-top-brokers__wrapper--sidebar {
  background: $c--gray-soft;
  border-radius: 6px;
  width: auto;
  padding: 24px;

  .a-top-brokers__title-disclaimer-sponsored-container {
    margin-bottom: 12px;
  }

  .a-top-brokers__broker-items {
    margin-top: 12px;
    display: grid;
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .a-top-brokers__broker-container {
    display: block;
    transition: none;
    border-radius: 6px;
    padding: 16px;
    border: 1px solid $c--gray-light;
    overflow: hidden;
    background: $c--white;
    text-align: center;
  }

  .a-top-brokers__broker-image-wrapper {
    width: 148px;
    margin: 0 auto 8px;
  }

  .a-top-brokers__placeholder-container {
    min-height: 66px;
  }
}

.a-top-brokers__wrapper {
  .a-top-brokers__title {
    color: $c--black;
  }

  .a-top-brokers__disclaimer-sponsored-container {
    margin-top: 12px;
    display: flex;

    /deep/ .disclaimer__wrapper {
      line-height: 10px;
    }
  }

  .a-top-brokers__disclaimer-icon-text-container {
    display: flex;
    align-items: center;

    &:hover {
      .a-top-brokers__disclaimer-label,
      .a-top-brokers__disclaimer-icon {
        color: $c--mid-purple;
        fill: $c--mid-purple;
      }
    }
  }

  .a-top-brokers__disclaimer-icon {
    fill: $disclaimer-sponsored-color;
  }

  .a-top-brokers__disclaimer-label {
    color: $disclaimer-sponsored-color;

    $margin-left: (
      $above-desktop-xl: 6px,
      $mobile: 4px
    );

    @include setStyleByBreakpoint('margin-left', $margin-left);
  }

  .a-top-brokers__disclaimer-text {
    font-size: 12px;
    font-weight: 500;
    line-height: 16px;
    letter-spacing: -0.15px;
    color: $c--gray-dark-2;
  }

  /deep/ .disclaimer-popup__wrapper {
    border: 2px solid $c--main;
    padding: 12px 16px;
    border-radius: 4px;
    background: $c--gray-soft;
  }

  /deep/ .disclaimer-popup__icon {
    display: none;
  }

  .a-top-brokers__sponsored-label {
    margin-right: 12px;
    color: $disclaimer-sponsored-color;
  }

  .a-top-brokers__placeholder-container {
    border-radius: 6px;
    border: 1px solid $c--gray-light;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    height: 100%;
  }

  .a-top-brokers__broker-image {
    border-radius: 4px;
  }

  .a-top-brokers__broker-title {
    font-size: 20px;
    font-weight: 500;
    line-height: 26px;

    @include tablet {
      font-size: 18px;
      line-height: 24px;
    }
  }

  .a-top-brokers__broker-button {
    margin: 6px auto 0;
    width: 130px;
    font-size: 14px;
  }
}
</style>
